import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Bundle, Practitioner } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { ComboboxItem, MultiSelect } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { getResourcesFromBundle } from "@/utils/medplum";
import { isSignatureInitialsLinkId } from "@/utils/questionnaires";
import { getName } from "@/utils/str";

interface QuestionnaireSignatureSelectFieldProps<T> {
  label?: string;
  form: UseFormReturnType<T>;
  name: string;
  placeholder?: string;
}

type SignatureSelectOption = ComboboxItem;

interface UseCurrentUserAutoSelectProps<T> {
  currentUser: ReturnType<typeof useMedplumProfile>;
  form: UseFormReturnType<T>;
  name: string;
  isInitialLoad: React.MutableRefObject<boolean>;
  hasUserInteracted: React.MutableRefObject<boolean>;
}

// Pre-selects the signed-in practitioner as soon as `currentUser` resolves, independent of the
// care team search below - `useMedplumProfile` already knows who's signed in without a network
// call, so the field that always requires a signer doesn't have to wait on an unrelated fetch.
// Only sets the form value; `careTeamOptions` always includes the current user via the derived
// `useMemo` below regardless of whether (or when) this runs, so there's nothing to race here.
const useCurrentUserAutoSelect = <T,>({
  currentUser,
  form,
  name,
  isInitialLoad,
  hasUserInteracted,
}: UseCurrentUserAutoSelectProps<T>) => {
  useEffect(() => {
    if (currentUser?.resourceType !== "Practitioner") {
      return;
    }
    if (!isInitialLoad.current || hasUserInteracted.current) {
      return;
    }
    const currentValue = form.values[name as keyof T];
    if (currentValue && (!Array.isArray(currentValue) || currentValue.length > 0)) {
      return;
    }
    const practitioner = currentUser as Practitioner;
    setFormFieldValue(form, name, [practitioner.id]);
    isInitialLoad.current = false;
  }, [currentUser, form, name, isInitialLoad, hasUserInteracted]);
};

export default function QuestionnaireSignatureSelectField<T>({
  label,
  form,
  name,
  placeholder,
}: QuestionnaireSignatureSelectFieldProps<T>) {
  const medplum = useMedplum();
  const currentUser = useMedplumProfile();
  const { clientId } = useParams() as { clientId: string };
  const [fetchedCareTeamOptions, setFetchedCareTeamOptions] = useState<SignatureSelectOption[]>([]);
  const isInitialLoad = useRef(true);
  const hasUserInteracted = useRef(false);
  useCurrentUserAutoSelect({ currentUser, form, name, isInitialLoad, hasUserInteracted });

  // Always includes the signed-in practitioner, however the fetched list is currently populated -
  // a pure derivation, so it can't go stale or drop them regardless of fetch timing or ordering.
  const careTeamOptions = useMemo(() => {
    if (currentUser?.resourceType !== "Practitioner") {
      return fetchedCareTeamOptions;
    }
    const practitioner = currentUser as Practitioner;
    const { fullName, initials } = getName(practitioner);
    const currentUserOption = {
      value: practitioner.id as string,
      label: `${initials} (${fullName})`,
    };
    if (fetchedCareTeamOptions.some((option) => option.value === currentUserOption.value)) {
      return fetchedCareTeamOptions;
    }
    return [...fetchedCareTeamOptions, currentUserOption];
  }, [fetchedCareTeamOptions, currentUser]);

  // Reset isInitialLoad when form values change. On mount this runs in the same commit right
  // after the pre-select effect above, before that effect's `setFormFieldValue` call has flushed
  // into `form.values` - so it can see the field as still empty and flip this ref back to `true`
  // one render before the value actually lands. That's harmless: the pre-select effect's own
  // guard checks the real form value (not just this ref), so it won't re-set once populated, and
  // `form` is a new object every render, so its effect re-evaluates on the very next render
  // regardless. Don't rely on this effect alone to gate whether pre-select should still run.
  useEffect(() => {
    const currentValue = form.values[name as keyof T];
    if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
      isInitialLoad.current = true;
    }
  }, [form.values, name]);

  useEffect(() => {
    let isCancelled = false;

    const fetchCareTeamByPatient = async (clientId: string) => {
      try {
        const bundle = await medplum.searchResources("CareTeam", {
          subject: `Patient/${clientId}`,
          status: "active",
          _include: "CareTeam:participant:Practitioner",
        });

        // Handle both array and bundle responses
        const properBundle = Array.isArray(bundle)
          ? {
              resourceType: "Bundle",
              entry: bundle.map((resource) => ({ resource })),
            }
          : bundle;

        const practitioners = getResourcesFromBundle<Practitioner>(
          "Practitioner",
          properBundle as unknown as Bundle<Practitioner>,
        );

        const includedPractitioners = practitioners.map((practitioner) => {
          const { fullName, initials } = getName(practitioner);
          return {
            value: practitioner.id as string,
            label: `${initials} (${fullName})`,
          };
        });

        if (isCancelled) {
          return;
        }
        setFetchedCareTeamOptions(includedPractitioners);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        console.error("Error fetching care team:", error);
      }
    };

    if (clientId) {
      fetchCareTeamByPatient(clientId);
    }

    return () => {
      isCancelled = true;
    };
  }, [medplum, clientId]);

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      // Set the value, allowing empty array to clear the signature
      setFormFieldValue(form, name, selectedValues);
      // Mark that user has interacted with the field
      hasUserInteracted.current = true;
    },
    [form, name],
  );

  // Get the value from form and ensure it's an array
  const value = form.values[name as keyof T];
  const multiSelectValue = Array.isArray(value) ? value : [];
  const error = form.getInputProps(name).error as React.ReactNode;
  // Mirrors the rule in getQuestionnaireSchema, so the asterisk only shows where a signer is
  // actually required — Transport's "Deciding Provider" shares this control but is optional.
  const isRequired = isSignatureInitialsLinkId(name);

  return (
    <MultiSelect
      clearable
      searchable
      name={name}
      label={label}
      error={error}
      data-testid={name}
      data={careTeamOptions}
      onChange={handleChange}
      maxDropdownHeight={400}
      value={multiSelectValue}
      withAsterisk={isRequired}
      nothingFoundMessage="No signatures found"
      placeholder={multiSelectValue.length === 0 ? (placeholder ?? "Select signatures") : ""}
    />
  );
}
